import { useCallback, useEffect, useState } from "react";
import { WarningCircle, CurrencyCircleDollar, Gear, Envelope, PauseCircle, FloppyDisk, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";
import { budgetStatus, updateBudget, resetBudgetAlert } from "../lib/api";
import { formatMoney, currencySymbol } from "../lib/currency";

const CURRENCY_OPTIONS = [
  "USD", "AUD", "CAD", "NZD", "GBP", "EUR", "SGD", "HKD", "JPY", "INR", "MXN", "BRL", "CHF",
];

const LEVEL_STYLES = {
  ok: {
    bar: "bg-emerald-400",
    text: "text-emerald-400",
    border: "border-emerald-400/30",
    label: "on track",
  },
  warning: {
    bar: "bg-amber-400",
    text: "text-amber-400",
    border: "border-amber-400/40",
    label: "warning",
  },
  exceeded: {
    bar: "bg-red-500",
    text: "text-red-400",
    border: "border-red-500/50",
    label: "exceeded",
  },
};

/**
 * Analytics-page budget card.
 * Shows MTD spend vs cap with a progress bar + edit dialog + reset-alert button.
 */
export default function BudgetCard() {
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      setStatus(await budgetStatus());
    } catch (err) {
      // Budget status is a soft signal — polling every 60s. A single
      // failed fetch is fine; leave last-known state in place.
      console.warn("BudgetCard: failed to refresh status:", err?.message || err);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60_000); // refresh every 60s
    return () => clearInterval(t);
  }, [load]);

  if (!status) return null;
  const style = LEVEL_STYLES[status.level] || LEVEL_STYLES.ok;
  const barWidth = Math.min(100, Math.max(0, status.percent));
  const ccy = status.display_currency || "USD";
  const spend = status.spend_display ?? status.spend.total_usd;
  const cap = status.cap_display ?? status.cap_usd;

  return (
    <div
      className={`bg-[#0a0a0a] border ${style.border} p-6 mb-6`}
      data-testid="budget-card"
    >
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <div className="label-mono flex items-center gap-2 mb-1">
            <CurrencyCircleDollar size={12} className={style.text} />
            monthly budget
            {status.budget?.active && (
              <span
                className={`text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 border ${style.border} ${style.text} bg-black/20`}
                data-testid="budget-level"
              >
                {style.label}
              </span>
            )}
            {!status.budget?.active && (
              <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 border border-zinc-700 text-zinc-500 bg-black/20">
                off
              </span>
            )}
          </div>
          <div className="flex items-end gap-2 mt-1">
            <div className={`font-display text-4xl font-black tracking-tighter ${style.text}`} data-testid="budget-spend">
              {formatMoney(spend, ccy)}
            </div>
            <div className="text-zinc-500 font-mono text-sm mb-1.5">
              / {formatMoney(cap, ccy)} · {status.percent}%
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {status.level !== "ok" && status.budget?.active && (
            <button
              data-testid="budget-reset-alert"
              onClick={async () => {
                await resetBudgetAlert();
                toast.success("Alert reset · next crossing re-fires");
                load();
              }}
              className="text-[10px] font-mono uppercase tracking-wider px-2.5 py-1.5 border border-white/10 text-zinc-400 hover:text-white hover:border-white/30 transition-colors"
            >
              reset alert
            </button>
          )}
          <button
            data-testid="budget-edit"
            onClick={() => setOpen(true)}
            className="text-[10px] font-mono uppercase tracking-wider px-2.5 py-1.5 border border-white/10 text-zinc-400 hover:text-[#ccff00] hover:border-[#ccff00]/40 transition-colors flex items-center gap-1"
          >
            <Gear size={11} />
            configure
          </button>
        </div>
      </div>

      {status.budget?.active && (
        <div className="h-2 bg-[#050505] border border-white/5 overflow-hidden mb-2">
          <div
            className={`h-full ${style.bar} transition-all duration-500`}
            style={{ width: `${barWidth}%` }}
            data-testid="budget-bar"
          />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-4 text-[10px] font-mono text-zinc-500">
        {status.budget?.active ? (
          <>
            <span>alert at {status.budget.alert_at_percent}%</span>
            <span>·</span>
            <span>auto-pause at {status.budget.pause_at_percent}%</span>
            {status.budget.notification_email && (
              <>
                <span>·</span>
                <span className="flex items-center gap-1">
                  <Envelope size={10} />
                  {status.budget.notification_email}
                </span>
              </>
            )}
            {status.paused_schedules > 0 && (
              <>
                <span>·</span>
                <span className="text-red-400 flex items-center gap-1">
                  <PauseCircle size={10} weight="fill" />
                  paused {status.paused_schedules} schedule{status.paused_schedules === 1 ? "" : "s"}
                </span>
              </>
            )}
            <span>·</span>
            <span>{status.spend.ad_sets} ad sets this month</span>
          </>
        ) : (
          <span>
            Budget guard is <span className="text-zinc-400">off</span>. Click{" "}
            <button
              onClick={() => setOpen(true)}
              className="text-[#ccff00] hover:underline"
            >
              configure
            </button>{" "}
            to set a cap.
          </span>
        )}
      </div>

      <BudgetDialog
        open={open}
        onOpenChange={setOpen}
        current={status.budget}
        onSaved={load}
      />
    </div>
  );
}

function BudgetDialog({ open, onOpenChange, current, onSaved }) {
  const [form, setForm] = useState({
    monthly_cap_usd: 25,
    alert_at_percent: 80,
    pause_at_percent: 100,
    notification_email: "",
    auto_pause_schedules: true,
    active: true,
    display_currency: "USD",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && current) {
      setForm({
        monthly_cap_usd: current.monthly_cap_usd ?? 25,
        alert_at_percent: current.alert_at_percent ?? 80,
        pause_at_percent: current.pause_at_percent ?? 100,
        notification_email: current.notification_email || "",
        auto_pause_schedules: !!current.auto_pause_schedules,
        active: !!current.active,
        display_currency: current.display_currency || "USD",
      });
    }
  }, [open, current]);

  const save = async () => {
    setSaving(true);
    try {
      await updateBudget({
        ...form,
        monthly_cap_usd: parseFloat(form.monthly_cap_usd) || 0,
        alert_at_percent: parseInt(form.alert_at_percent, 10) || 80,
        pause_at_percent: parseInt(form.pause_at_percent, 10) || 100,
        notification_email: form.notification_email?.trim() || null,
        display_currency: form.display_currency.toUpperCase(),
      });
      toast.success("Budget saved");
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#0a0a0a] border border-white/10 rounded-none max-w-md"
        data-testid="budget-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <CurrencyCircleDollar size={22} weight="fill" className="text-amber-400" />
            Monthly budget guard
          </DialogTitle>
          <DialogDescription className="text-zinc-500 text-sm">
            Set a monthly LLM cap. We&apos;ll warn you at your alert threshold and auto-pause all active schedules at the pause threshold.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox
              data-testid="budget-active-checkbox"
              checked={form.active}
              onCheckedChange={(v) => setForm({ ...form, active: !!v })}
            />
            <span className="text-sm text-zinc-300">Enable budget guard</span>
          </label>
          <div>
            <div className="label-mono mb-2">monthly cap (USD — the underlying unit)</div>
            <Input
              data-testid="budget-cap-input"
              type="number"
              step="0.5"
              min="0.01"
              value={form.monthly_cap_usd}
              onChange={(e) => setForm({ ...form, monthly_cap_usd: e.target.value })}
              className="bg-[#121212] border-white/10 h-10 rounded-none font-mono"
            />
          </div>
          <div>
            <div className="label-mono mb-2">display currency</div>
            <select
              data-testid="budget-currency-select"
              value={form.display_currency}
              onChange={(e) => setForm({ ...form, display_currency: e.target.value })}
              className="bg-[#121212] border border-white/10 h-10 rounded-none font-mono text-sm w-full px-3 text-white"
            >
              {CURRENCY_OPTIONS.map((c) => (
                <option key={c} value={c}>{c} · {currencySymbol(c).trim() || c}</option>
              ))}
            </select>
            <div className="text-[10px] font-mono text-zinc-600 mt-1">
              Shown across revenue totals, cost badges, and this budget card.
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="label-mono mb-2">warn at %</div>
              <Input
                data-testid="budget-alert-input"
                type="number"
                min="1"
                max="200"
                value={form.alert_at_percent}
                onChange={(e) => setForm({ ...form, alert_at_percent: e.target.value })}
                className="bg-[#121212] border-white/10 h-10 rounded-none font-mono"
              />
            </div>
            <div>
              <div className="label-mono mb-2">pause at %</div>
              <Input
                data-testid="budget-pause-input"
                type="number"
                min="1"
                max="300"
                value={form.pause_at_percent}
                onChange={(e) => setForm({ ...form, pause_at_percent: e.target.value })}
                className="bg-[#121212] border-white/10 h-10 rounded-none font-mono"
              />
            </div>
          </div>
          <div>
            <div className="label-mono mb-2">notification email (optional)</div>
            <Input
              data-testid="budget-email-input"
              type="email"
              value={form.notification_email}
              onChange={(e) => setForm({ ...form, notification_email: e.target.value })}
              placeholder="you@example.com"
              className="bg-[#121212] border-white/10 h-10 rounded-none font-mono text-xs"
            />
            <div className="text-[10px] font-mono text-zinc-600 mt-1">
              Sent via Resend once per threshold crossing (never spammy).
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox
              data-testid="budget-autopause-checkbox"
              checked={form.auto_pause_schedules}
              onCheckedChange={(v) => setForm({ ...form, auto_pause_schedules: !!v })}
            />
            <span className="text-sm text-zinc-300">Auto-pause all schedules when exceeded</span>
          </label>
        </div>
        <DialogFooter>
          <button
            data-testid="budget-save-btn"
            onClick={save}
            disabled={saving}
            className="bg-[#ccff00] text-black hover:bg-[#b3e600] disabled:opacity-40 font-semibold py-2.5 px-4 flex items-center gap-2"
          >
            <FloppyDisk size={13} weight="fill" />
            {saving ? "Saving…" : "Save"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Sticky top-of-page warning banner. Auto-loads status; hides itself on 'ok'. */
export function BudgetBanner() {
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  const load = useCallback(async () => {
    try {
      setStatus(await budgetStatus());
    } catch (err) {
      console.warn("BudgetBanner: failed to refresh status:", err?.message || err);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, [load]);

  if (!status || !status.budget?.active || status.level === "ok" || dismissed) return null;
  const style = LEVEL_STYLES[status.level];
  const ccy = status.display_currency || "USD";
  const spendStr = formatMoney(status.spend_display ?? status.spend.total_usd, ccy);
  const capStr = formatMoney(status.cap_display ?? status.cap_usd, ccy);
  return (
    <div
      className={`sticky top-0 z-30 border-b ${style.border} bg-black/95 backdrop-blur px-4 py-2.5`}
      data-testid={`budget-banner-${status.level}`}
    >
      <div className="max-w-6xl mx-auto flex items-center gap-3 text-sm">
        <WarningCircle size={16} weight="fill" className={style.text} />
        <div className="flex-1 min-w-0">
          <span className={`font-bold ${style.text} uppercase tracking-wider text-[11px] font-mono`}>
            {status.level === "exceeded" ? "Budget exceeded" : "Budget warning"}
          </span>
          <span className="text-zinc-400 ml-2 font-mono text-xs">
            {spendStr} / {capStr} · {status.percent}% this month
            {status.paused_schedules > 0 && ` · ${status.paused_schedules} schedule(s) auto-paused`}
          </span>
        </div>
        <a
          href="/analytics"
          className={`text-[11px] font-mono uppercase tracking-wider hover:underline ${style.text}`}
        >
          view
        </a>
        <button
          onClick={() => setDismissed(true)}
          data-testid="budget-banner-close"
          className="text-zinc-500 hover:text-white transition-colors"
          aria-label="dismiss"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
